<script setup lang="ts">
import { getCookie, setCookie } from '@@/bits/cookie'
import device from '@@/bits/device'
import { ANXIOUS_FACE_EMOJI, FACE_WITH_ROLLING_EYES_EMOJI } from '@@/bits/emoji'
import { ww } from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { getLibraryMembershipRoleTranslation } from '@@/bits/libraries_helper'
import { navigateTo, transformCurrentUrl } from '@@/bits/location'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import OzPadletLogo from '@@/library/v4/components/OzPadletLogo.vue'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useLibraryAcceptInviteStore } from '@@/pinia/library_accept_invite'
import AuthBottomNav from '@@/vuecomponents/AuthBottomNav.vue'
import GlobalAlertDialog from '@@/vuecomponents/GlobalAlertDialog.vue'
import GlobalConfirmationDialog from '@@/vuecomponents/GlobalConfirmationDialog.vue'
import GlobalSnackbar from '@@/vuecomponents/GlobalSnackbar.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, watch } from 'vue'

const { openAlertDialog } = useGlobalAlertDialogStore()
const { openConfirmationDialog } = useGlobalConfirmationDialogStore()
const libraryAcceptInviteStore = useLibraryAcceptInviteStore()
const {
  user,
  role,
  slug,
  isActiveLibraryMember,
  code,
  hasAcceptedInvite,
  isAwaitingServerResponse,
  isAwaitingRejection,
  errorMessage,
  isUserLoggedIn,
  exceededQuota,
  library,
  isClassroomLibrary,
  isSchoolLibrary,
  email,
  goToLogin,
  disableAlreadyEnrolledAlert,
  isReseller,
  inviteRejectionEnabled,
} = storeToRefs(libraryAcceptInviteStore)

// Check if library has separate admin and teacher caps (for now, only PD orgs have this)
const hasIndividualRoleCaps = computed<boolean>(() => library.value.customSchoolLibraryType === 'pd_org')
const { initializeState, goToLibraryDashboardOrMakePage, acceptInvite, rejectInvite } = libraryAcceptInviteStore

const isDesktop = device.desktop

const alreadyMemberOfLibraryText = computed<string>(() => {
  if (isClassroomLibrary.value) return __('You’re already a member of this classroom')
  if (isSchoolLibrary.value) return __('You’re already a member of this school')
  return __('You’re already a member of this team')
})

const libraryType = computed<string>(() => {
  if (isClassroomLibrary.value) return __('Classroom')
  if (isSchoolLibrary.value) return __('School')
  return __('Team')
})

const acceptInviteText = computed<string>(() => {
  if (isClassroomLibrary.value) return __('Accept classroom invitation')
  if (isSchoolLibrary.value) return __('Accept school invitation')
  return __('Accept team invitation')
})

const errorAddingToLibraryText = computed<string>(() => {
  if (isClassroomLibrary.value) return __('There was an error adding you to the classroom.')
  if (isSchoolLibrary.value) return __('There was an error adding you to the school.')
  return __('There was an error adding you to the team.')
})

const addingToLibraryText = computed<string>(() => {
  if (isClassroomLibrary.value) return __('Adding you to the classroom...')
  if (isSchoolLibrary.value) {
    if (library.value.name !== null) {
      return __('Adding you to %{libraryName}...', { libraryName: library.value.name })
    } else {
      return __('Adding you to the school...')
    }
  }
  return __('Adding you to the team...')
})

const invitedToLibraryText = computed<string>(() => {
  if (isClassroomLibrary.value) return __('You’ve been invited to a classroom')
  if (isSchoolLibrary.value) return __('You’ve been invited to a school')
  return __('You’ve been invited to a team')
})

const tryAgainText = computed<string>(() => {
  return __('Try again')
})

const DISMISSED_INVITE_COOKIE = 'dismissed_library_invite_ids'
const DISMISSED_INVITE_COOKIE_MAX_AGE = 30 * 24 * 60 * 60 // 30 days

function dismissInvite(): void {
  const libraryId = String(library.value.id)
  const existing = getCookie(DISMISSED_INVITE_COOKIE)
  const ids = existing ? existing.split(',') : []
  if (!ids.includes(libraryId)) {
    ids.push(libraryId)
  }
  setCookie(DISMISSED_INVITE_COOKIE, ids.join(','), { maxAgeInSeconds: DISMISSED_INVITE_COOKIE_MAX_AGE })
}

function dismissAndGoToHomepage(): void {
  dismissInvite()
  navigateTo('/')
}

function confirmRejectInvite(): void {
  openConfirmationDialog({
    title: __('Reject this invite?'),
    body: __('You will be removed from the invitation list. The person who invited you will be notified.'),
    confirmButtonText: __('Reject invite'),
    cancelButtonText: __('Keep invite'),
    buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    forceFullWidthButtons: true,
    afterConfirmCallbacks: [
      async () => {
        try {
          await rejectInvite()
          navigateTo('/')
        } catch {
          useGlobalSnackbarStore().genericFetchError()
        }
      },
    ],
  })
}

function goToAuthPage(): void {
  const searchParams: { referrer: string; library_invite_code?: string; email?: string; school_library_id?: string } = {
    referrer: window.location.href,
    library_invite_code: code.value,
  }
  if (email.value) {
    searchParams.email = email.value
  }
  if (isSchoolLibrary.value) {
    searchParams.school_library_id = String(library.value.id)
  }

  const path = goToLogin.value ? '/auth/login' : '/auth/signup'

  const authUrl = transformCurrentUrl(
    {},
    {
      path,
      search: searchParams,
    },
  )
  navigateTo(authUrl)
}

watch([isAwaitingServerResponse], () => {
  const html = document.getElementsByTagName('html')[0]

  // Animate the background color while waiting for the response
  html.classList.toggle('dot-grid-loading', isAwaitingServerResponse.value)
})

onMounted(() => {
  initializeState(ww.vueStartingState)

  if (!disableAlreadyEnrolledAlert.value) {
    if (isActiveLibraryMember.value) {
      return openAlertDialog({
        iconSrc: library.value.avatar,
        title: library.value.name,
        body: alreadyMemberOfLibraryText.value,
        closeButtonText: __('Continue'),
        afterCloseActions: [goToLibraryDashboardOrMakePage],
        xShadow: true,
      })
    }
  }

  if (exceededQuota.value) {
    let exceededQuotaAlertTitle
    let exceededQuotaAlertBody

    if (hasIndividualRoleCaps.value) {
      // Libraries with separate admin and teacher caps show role-specific messages
      if (role.value === 'admin') {
        exceededQuotaAlertTitle = __('This school has reached its admin limit')
      } else if (role.value === 'teacher') {
        exceededQuotaAlertTitle = __('This school has reached its teacher limit')
      } else {
        exceededQuotaAlertTitle = __('This school has reached its limit')
      }
      exceededQuotaAlertBody = __('Please contact the owner of the school to add more seats')
    } else if (isReseller.value) {
      exceededQuotaAlertTitle = __('This school has reached its teacher and admin limit')
      exceededQuotaAlertBody = __('Please contact your reseller to add more seats')
    } else if (isSchoolLibrary.value) {
      exceededQuotaAlertTitle = __('This school has reached its teacher and admin limit')
      exceededQuotaAlertBody = __('Please contact the owner of the school to add more seats')
    } else if (role.value === 'teacher') {
      exceededQuotaAlertTitle = __('This classroom has reached its teacher limit')
      exceededQuotaAlertBody = __('Please contact the owner of the classroom about this issue.')
    } else {
      exceededQuotaAlertTitle = __('This classroom has reached its student limit')
      exceededQuotaAlertBody = __('Please contact a teacher in the classroom about this issue.')
    }

    return openAlertDialog({
      iconSrc: FACE_WITH_ROLLING_EYES_EMOJI.iconSrc,
      title: exceededQuotaAlertTitle,
      body: exceededQuotaAlertBody,
      closeButtonText: __('Go home'),
      afterCloseActions: [dismissAndGoToHomepage],
      xShadow: true,
    })
  }

  if (hasAcceptedInvite.value && isUserLoggedIn.value) {
    waitForAcceptInvite()
  }
})

async function waitForAcceptInvite(): Promise<void> {
  await acceptInvite()
}
</script>

<template>
  <div
    :class="[
      'relative',
      'flex flex-col items-center',
      'w-full h-full',
      'tablet-portrait:justify-center',
      { 'tablet-portrait:items-start': isAwaitingServerResponse || errorMessage },
    ]"
  >
    <header
      :class="[
        'fixed',
        'top-0',
        'flex',
        'items-center',
        'justify-between',
        'w-full',
        'box-border',
        'h-14',
        'ps-5',
        'pe-6',
      ]"
    >
      <SkipToContentLink />

      <OzPadletLogo />

      <div v-if="isUserLoggedIn" class="flex pt-4 self-start">
        <OzAvatar
          class="rounded-full"
          :width="32"
          :height="32"
          :src="user.avatar"
          :alt-text-name="user.name || user.username"
        />

        <div class="flex flex-col ms-3">
          <span class="text-body-small font-semibold text-dark-text-100 dark:text-light-text-100">{{
            user.name || user.username
          }}</span>
          <span class="text-body-extra-small text-dark-text-200 dark:text-light-text-200">{{ user.email }}</span>
        </div>
      </div>
    </header>

    <main
      id="main-content"
      :class="[
        'fixed bottom-0',
        'flex',
        'w-full box-border',
        'px-4 pt-4 pb-6',
        'tablet-portrait:relative tablet-portrait:w-[390px] tablet-portrait:p-0 tablet-portrait:px-4',
        {
          'tablet-landscape:ms-auto tablet-landscape:me-[50vw] tablet-landscape:rtl:ms-[50vw] tablet-landscape:rtl:me-auto':
            isAwaitingServerResponse || errorMessage,
        },
      ]"
      tabindex="-1"
    >
      <h1 class="sr-only">{{ acceptInviteText }}</h1>
      <!-- Error UI -->
      <div
        v-if="errorMessage"
        :class="[
          'flex flex-col items-start grow',
          'text-dark-text-100 dark:text-light-text-100',
          'tablet-portrait:ms-5 tablet-landscape:ms-0',
        ]"
      >
        <img :alt="errorAddingToLibraryText" width="64" height="64" :src="ANXIOUS_FACE_EMOJI.iconSrc" class="ms-2" />
        <p class="text-heading-2 font-semibold mb-1 ms-2">{{ __('Oops!') }}</p>
        <p class="text-17-22 mb-6 ms-2">
          {{ errorAddingToLibraryText }}
        </p>
        <OzContainedButton
          :color-scheme="OzContainedButtonColorScheme.Primary"
          class="w-full mb-2 tablet-portrait:w-auto"
          @click="acceptInvite()"
          >{{ tryAgainText }}
        </OzContainedButton>
      </div>

      <!-- Loading UI -->
      <div
        v-else-if="isAwaitingServerResponse"
        :class="[
          'flex flex-col grow',
          'text-dark-text-100 dark:text-light-text-100',
          'ms-2 tablet-portrait:ms-7 tablet-landscape:ms-0',
        ]"
      >
        <OzIndeterminateSpinner :size="33" class="mb-4" />
        <p class="text-heading-2 font-semibold mb-3" data-testid="addingToLibraryText">
          {{ addingToLibraryText }}
        </p>
        <p class="text-body-posts">
          {{ __('It’s all sunshine and rainbows from here.') }}
        </p>
      </div>
      <!-- Accept invite UI -->
      <div v-else-if="library.id" :class="['flex flex-col w-full', 'text-dark-text-100 dark:text-light-text-100']">
        <p class="w-2/3 text-login-heading-1 font-semibold ms-2 mb-8">
          {{ invitedToLibraryText }}
        </p>

        <div class="flex flex-col mb-8">
          <p class="text-body-small font-semibold mb-2 ps-2">{{ libraryType }}</p>

          <OzBox :color="OzBoxColors.Secondary" :radius="12" class="flex p-2">
            <OzAvatar class="rounded-xl" :width="48" :height="48" :src="library.avatar" :alt-text-name="library.name" />

            <div class="flex flex-col ms-4 overflow-hidden">
              <span class="text-heading-3 font-semibold truncate">{{ library.name }}</span>
              <span class="text-body-small text-dark-text-200 dark:text-light-text-200">{{ slug }}</span>
            </div>
          </OzBox>
        </div>

        <div
          :class="[
            'flex flex-col',
            'text-dark-text-100 dark:text-light-text-100',
            'mb-10 ps-2',
            'tablet-portrait:mb-16',
          ]"
        >
          <span class="text-body-small font-semibold mb-2">{{ __('Role') }}</span>
          <span class="text-body">{{ getLibraryMembershipRoleTranslation(role) }}</span>
        </div>

        <div
          :class="['flex justify-center flex-col', 'tablet-portrait:flex-row-reverse tablet-portrait:justify-start']"
        >
          <OzContainedButton
            v-if="isUserLoggedIn"
            data-testid="acceptInviteButton"
            :color-scheme="OzContainedButtonColorScheme.Primary"
            :text="__('Accept invite')"
            :should-truncate-text="false"
            :auto-height="true"
            class="w-full tablet-portrait:w-auto"
            @click="acceptInvite"
          />
          <OzContainedButton
            v-else
            data-testid="logInToAcceptInviteButton"
            :color-scheme="OzContainedButtonColorScheme.Primary"
            :text="goToLogin ? __('Log in to accept invite') : __('Sign up to accept invite')"
            :should-truncate-text="false"
            :auto-height="true"
            class="w-full tablet-portrait:w-auto"
            @click="goToAuthPage"
          />

          <OzContainedButton
            :color-scheme="OzContainedButtonColorScheme.Secondary"
            :text="inviteRejectionEnabled && isUserLoggedIn ? __('Reject') : __('Not now')"
            :should-truncate-text="false"
            :auto-height="true"
            :disabled="isAwaitingRejection"
            :class="['w-full mt-2', 'tablet-portrait:mt-0 tablet-portrait:w-auto tablet-portrait:me-2']"
            @click="inviteRejectionEnabled && isUserLoggedIn ? confirmRejectInvite() : dismissAndGoToHomepage()"
          />
        </div>
      </div>
      <GlobalAlertDialog />
      <GlobalConfirmationDialog />
      <GlobalSnackbar />
    </main>

    <AuthBottomNav v-if="isDesktop" class="fixed start-6 bottom-6 hidden tablet-portrait:flex" />
  </div>
</template>

<style lang="postcss">
@import '@@/styles/4/kit';
@import '@@/styles/4/dot_grid';

* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html {
  background: var(--dot-grid);
  @apply overflow-hidden;
  background-position: start top;
  @apply h-screen;

  font-size: 16px;
}

@media screen and (orientation: landscape) {
  html {
    @apply overflow-auto;
  }
}
</style>
